import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import {
  CreateProviderTemplatePayload,
  ProviderTemplate,
  TestProviderTemplatePayload,
  UpdateProviderTemplatePayload,
} from '@/client/types/notifications-infra';
import {
  normalizeProviderTemplate,
  notificationsInfraKeys,
  unwrapNotifications,
} from './notifications.query';

const BASE = '/api/admin/v2/notifications';
const TIMEOUT = { timeout: 45_000 };

const asProviderTemplate = (data: unknown, fallbackId?: string | number): ProviderTemplate =>
  normalizeProviderTemplate(data, fallbackId);

const invalidateProvider = (
  qc: ReturnType<typeof useQueryClient>,
  provider: string,
  templateId?: string | number,
) => {
  qc.invalidateQueries({ queryKey: notificationsInfraKeys.templates(provider) });
  qc.invalidateQueries({ queryKey: notificationsInfraKeys.reconciliation() });
  qc.invalidateQueries({ queryKey: notificationsInfraKeys.overview() });
  if (templateId != null) {
    qc.invalidateQueries({
      queryKey: notificationsInfraKeys.template(provider, String(templateId)),
    });
  }
};

export const useCreateProviderTemplateMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: {
      provider: string;
      body: CreateProviderTemplatePayload;
      idempotencyKey?: string;
    }): Promise<ProviderTemplate> => {
      const raw = await request<
        APIResponse<ProviderTemplate | { template?: ProviderTemplate }> | ProviderTemplate
      >(
        `${BASE}/providers/${encodeURIComponent(args.provider)}/templates`,
        'POST',
        args.body,
        { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
      );
      return asProviderTemplate(unwrapNotifications(raw));
    },
    'Failed to create provider template',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { provider }) => invalidateProvider(qc, provider),
  });
};

export const useUpdateProviderTemplateMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: {
      provider: string;
      id: string | number;
      body: UpdateProviderTemplatePayload;
      idempotencyKey?: string;
    }): Promise<ProviderTemplate> => {
      const raw = await request<
        APIResponse<ProviderTemplate | { template?: ProviderTemplate }> | ProviderTemplate
      >(
        `${BASE}/providers/${encodeURIComponent(args.provider)}/templates/${encodeURIComponent(String(args.id))}`,
        'PATCH',
        args.body,
        { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
      );
      return asProviderTemplate(unwrapNotifications(raw), args.id);
    },
    'Failed to update provider template',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { provider, id }) => invalidateProvider(qc, provider, id),
  });
};

export const useDeleteProviderTemplateMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: {
      provider: string;
      id: string | number;
      idempotencyKey?: string;
    }): Promise<{ success?: boolean }> => {
      const raw = await request<APIResponse<{ success?: boolean }> | { success?: boolean }>(
        `${BASE}/providers/${encodeURIComponent(args.provider)}/templates/${encodeURIComponent(String(args.id))}`,
        'DELETE',
        {},
        { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
      );
      return unwrapNotifications(raw) ?? {};
    },
    'Failed to delete provider template',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { provider, id }) => invalidateProvider(qc, provider, id),
  });
};

export const useTestProviderTemplateMutation = () => {
  const request = useClientStore((s) => s.request);
  const fn = withErrorHandling(
    async (args: {
      provider: string;
      id: string | number;
      body: TestProviderTemplatePayload;
      idempotencyKey?: string;
    }) => {
      const raw = await request<APIResponse<unknown> | unknown>(
        `${BASE}/providers/${encodeURIComponent(args.provider)}/templates/${encodeURIComponent(String(args.id))}/test`,
        'POST',
        args.body,
        { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
      );
      return unwrapNotifications(raw);
    },
    'Failed to send test email',
  );
  return useMutation({ mutationFn: fn });
};
